<template>
  <div class="page-container">
    <div class="bg-grid"></div>
    <div class="bg-glow"></div>

    <header class="page-header">
      <div class="breadcrumb">
        <router-link to="/" class="breadcrumb-link">{{ $i18n.locale === 'zh' ? '首页' : 'Home' }}</router-link>
        <span class="breadcrumb-sep">/</span>
        <span class="breadcrumb-current">{{ $i18n.t('pages.timestamp.title') }}</span>
      </div>
      <div class="header-content">
        <div class="header-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
            <line x1="2" y1="12" x2="22" y2="12"/>
          </svg>
        </div>
        <h1 class="page-title">{{ $i18n.t('pages.timestamp.title') }}</h1>
        <p class="page-subtitle">{{ $i18n.t('pages.timestamp.subtitle') }}</p>
      </div>
    </header>

    <main class="page-content">
      <div class="converter-card">
        <div class="conversion-section">
          <div class="section-header">
            <span class="section-tag">TS → DATE</span>
            <h3>{{ $i18n.t('pages.timestamp.tsToDate') }}</h3>
          </div>
          <div class="input-row">
            <input
              type="text"
              v-model="timestampInput"
              :placeholder="$i18n.t('pages.timestamp.placeholder')"
              class="input-field"
            />
            <button @click="convertToDate" class="convert-btn">{{ $i18n.t('pages.timestamp.convert') }}</button>
          </div>
          <div v-if="dateResult" class="result-box">
            <span class="result-label">{{ $i18n.locale === 'zh' ? '转换结果' : 'Result' }}</span>
            <span class="result-value">{{ dateResult }}</span>
          </div>
        </div>

        <div class="conversion-section">
          <div class="section-header">
            <span class="section-tag">DATE → TS</span>
            <h3>{{ $i18n.t('pages.timestamp.dateToTs') }}</h3>
          </div>
          <div class="input-row">
            <input
              type="datetime-local"
              v-model="datetimeInput"
              class="input-field"
            />
            <button @click="convertToTimestamp" class="convert-btn">{{ $i18n.t('pages.timestamp.convert') }}</button>
          </div>
          <div v-if="timestampResult" class="result-box">
            <span class="result-label">{{ $i18n.locale === 'zh' ? '转换结果' : 'Result' }}</span>
            <span class="result-value">{{ timestampResult }}</span>
          </div>
        </div>

        <div class="current-section">
          <div class="section-header">
            <span class="section-tag live">LIVE</span>
            <h3>{{ $i18n.t('pages.timestamp.currentTime') }}</h3>
          </div>
          <div class="current-time-display">
            <div class="time-block">
              <span class="time-value">{{ currentTimestamp }}</span>
              <span class="time-label">{{ $i18n.t('pages.timestamp.timestampLabel') }}</span>
            </div>
            <div class="time-divider"></div>
            <div class="time-block">
              <span class="time-value date">{{ currentDateTime }}</span>
              <span class="time-label">{{ $i18n.t('pages.timestamp.dateLabel') }}</span>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'TimestampConverterPage',
  inject: ['i18n'],
  data() {
    return {
      timestampInput: '',
      datetimeInput: '',
      dateResult: '',
      timestampResult: '',
      currentTimestamp: '',
      currentDateTime: ''
    }
  },
  methods: {
    convertToDate() {
      if (!this.timestampInput) {
        return
      }
      const ts = parseInt(this.timestampInput)
      if (isNaN(ts)) {
        return
      }
      const timestamp = this.timestampInput.length === 13 ? ts : ts * 1000
      const date = new Date(timestamp)
      if (isNaN(date.getTime())) {
        return
      }
      const opt = {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      }
      this.dateResult = date.toLocaleString(this.$i18n.locale === 'zh' ? 'zh-CN' : 'en-US', opt)
    },
    convertToTimestamp() {
      if (!this.datetimeInput) {
        return
      }
      const date = new Date(this.datetimeInput)
      if (isNaN(date.getTime())) {
        return
      }
      this.timestampResult = Math.floor(date.getTime() / 1000)
    },
    updateTime() {
      const now = new Date()
      const opt = {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      }
      this.currentTimestamp = Math.floor(now.getTime() / 1000)
      this.currentDateTime = now.toLocaleString(this.$i18n.locale === 'zh' ? 'zh-CN' : 'en-US', opt)
    }
  },
  mounted() {
    this.updateTime()
    this.intervalId = setInterval(() => this.updateTime(), 1000)
  },
  beforeUnmount() {
    clearInterval(this.intervalId)
  }
}
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  padding: 40px 24px 60px;
  position: relative;
  overflow: hidden;
}

.bg-grid {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    linear-gradient(rgba(232, 255, 0, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(232, 255, 0, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.bg-glow {
  position: fixed;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(0, 240, 255, 0.08) 0%, transparent 70%);
  top: 20%;
  right: -200px;
  filter: blur(100px);
  pointer-events: none;
  z-index: 0;
}

.page-header {
  max-width: 700px;
  margin: 0 auto 50px;
  position: relative;
  z-index: 1;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 30px;
  font-family: var(--font-mono);
  font-size: 12px;
}

.breadcrumb-link {
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.3s;
}

.breadcrumb-link:hover {
  color: var(--accent-primary);
}

.breadcrumb-sep {
  color: var(--border-color);
}

.breadcrumb-current {
  color: var(--accent-success);
}

.header-content {
  text-align: center;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  color: var(--accent-success);
  margin-bottom: 20px;
}

.page-title {
  font-family: var(--font-display);
  font-size: 48px;
  letter-spacing: 2px;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  font-weight: 300;
}

.page-content {
  max-width: 700px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.converter-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 40px;
}

.conversion-section {
  margin-bottom: 40px;
  padding-bottom: 40px;
  border-bottom: 1px solid var(--border-color);
}

.conversion-section:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.section-tag {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 1px;
  padding: 4px 8px;
  background: var(--bg-secondary);
  border-radius: 4px;
  color: var(--accent-success);
}

.section-tag.live {
  background: rgba(0, 240, 255, 0.1);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.section-header h3 {
  font-family: var(--font-body);
  font-size: 18px;
  font-weight: 500;
  color: var(--text-primary);
  margin: 0;
}

.input-row {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.input-field {
  flex: 1;
  padding: 16px 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  color: var(--text-primary);
  font-family: var(--font-mono);
  font-size: 15px;
  transition: border-color 0.3s;
}

.input-field::placeholder {
  color: var(--text-secondary);
}

.input-field:focus {
  outline: none;
  border-color: var(--accent-success);
}

.convert-btn {
  padding: 16px 28px;
  background: var(--accent-success);
  color: var(--bg-primary);
  border: none;
  border-radius: 12px;
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.convert-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 240, 255, 0.3);
}

.result-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: var(--bg-secondary);
  border-radius: 12px;
  border-left: 3px solid var(--accent-success);
}

.result-label {
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.result-value {
  font-family: var(--font-mono);
  font-size: 16px;
  color: var(--accent-success);
}

.current-section {
  background: var(--bg-secondary);
  border-radius: 16px;
  padding: 28px;
}

.current-time-display {
  display: flex;
  align-items: center;
  gap: 24px;
}

.time-block {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.time-value {
  font-family: var(--font-mono);
  font-size: 28px;
  font-weight: 700;
  color: var(--accent-success);
  letter-spacing: 1px;
}

.time-value.date {
  font-size: 20px;
}

.time-label {
  font-size: 12px;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.time-divider {
  width: 1px;
  height: 60px;
  background: var(--border-color);
}

@media (max-width: 640px) {
  .page-container {
    padding: 24px 16px 40px;
  }

  .page-title {
    font-size: 36px;
  }

  .converter-card {
    padding: 24px;
  }

  .input-row {
    flex-direction: column;
  }

  .convert-btn {
    width: 100%;
  }

  .current-time-display {
    flex-direction: column;
    gap: 16px;
  }

  .time-divider {
    width: 100%;
    height: 1px;
  }
}
</style>
